import React, { InputHTMLAttributes, ReactNode, forwardRef, ForwardedRef } from 'react';

interface TeslaInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Appends a red * after the label for required fields */
  showRequired?: boolean;
  icon?: ReactNode;
  actionIcon?: ReactNode;
  onActionClick?: () => void;
  error?: string;
  fullWidth?: boolean;
  className?: string;
}

/**
 * Tesla-inspired minimalist input component
 */
const TeslaInput = forwardRef((
  {
    label,
    showRequired,
    icon,
    actionIcon,
    onActionClick,
    error,
    fullWidth = false,
    className = '',
    id,
    ...props
  }: TeslaInputProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  // Generate a unique ID if none is provided
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  // Determine input container classes based on props
  const containerClasses = [
    'input-container',
    icon ? 'input-container--with-icon' : '',
    actionIcon ? 'input-container--with-action' : '',
    error ? 'input-container--error' : '',
    fullWidth ? 'input-container--full' : '',
    className
  ].filter(Boolean).join(' ');

  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className={containerClasses}>
      {label && (
        <label htmlFor={inputId} className="input-label">
          {label}
          {showRequired ? <span className="input-label-required" aria-hidden> *</span> : null}
        </label>
      )}
      
      <div className="input-wrapper">
        {icon && <span className="input-icon">{icon}</span>}
        
        <input
          ref={ref}
          id={inputId}
          className="input-field"
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          aria-required={showRequired ? true : undefined}
          {...props}
        />
        
        {actionIcon && (
          <button 
            type="button" 
            className="input-action" 
            onClick={onActionClick}
            tabIndex={-1}
          >
            {actionIcon}
          </button>
        )}
      </div>
      
      {error && (
        <div className="input-error" id={errorId} role="alert">
          {error}
        </div>
      )}
    </div>
  );
});

TeslaInput.displayName = 'TeslaInput';

export default TeslaInput;